/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */
import * as React from "react";
import styled from "styled-components";
import { SVGIcon, Tooltip } from "@nordcloud/gnui";
import { CloudProviderType, Provider } from "~/generated/graphql";
import { getAccountIconStatus } from "~/tools";

type TooltipPosition = "left" | undefined;

const StyledDiv = styled.div`
  display: inline-block;
`;

type Props = {
  provider: Provider;
  providerType?: CloudProviderType;
  activated: boolean;
  status: string;
};

export function AccountStatusIcon({
  provider,
  providerType,
  activated,
  status,
}: Props) {
  const { width, color, comment, name } = getAccountIconStatus({
    provider,
    active: activated,
    status,
    providerType,
  });

  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<TooltipPosition>();

  const getTooltipPosition = () => {
    const tooltipSize = tooltipRef.current?.getBoundingClientRect();
    return tooltipSize !== undefined && tooltipSize.x <= 150
      ? "left"
      : undefined;
  };

  React.useEffect(() => {
    const handlePosition = () => setPosition(getTooltipPosition());
    handlePosition();
    window.addEventListener("resize", handlePosition);
    return () => window.removeEventListener("resize", handlePosition);
  }, [position]);

  return (
    <StyledDiv ref={tooltipRef}>
      <Tooltip
        minWidth={width}
        status={color}
        caption={comment}
        hideTimeout={0}
        position={position}
      >
        <SVGIcon color={color} name={name} css={{ verticalAlign: "sub" }} />
      </Tooltip>
    </StyledDiv>
  );
}
